/*
 * 🔄 API ADAPTER - SUBSTITUIÇÃO COMPLETA DO FIREBASE
 * Mantém 100% compatibilidade com o código existente
 * Migração: Firebase → PostgreSQL + Node.js
 */

import axios from 'axios'
import config from '@/config/environment'
// 🔥 Importar sistema centralizado de error handling
import errorHandlingService from '@/services/errorHandlingService'
// 🔄 Importar sistema centralizado de loading
import loadingService from '@/services/loadingService'

// 🌍 Configuração da API usando environment config
const BASE_URL = config.API.API_URL

console.log('🚀 API Configuration:', {
  BASE_URL,
  TIMEOUT: config.API.REQUEST_TIMEOUT,
  DEBUG_MODE: config.MONITORING.DEBUG_MODE
})

// 📦 INSTÂNCIA AXIOS CONFIGURADA
const api = axios.create({
  baseURL: BASE_URL,
  timeout: config.API.REQUEST_TIMEOUT,
  headers: config.API.DEFAULT_HEADERS,
  withCredentials: true
})

// 🔄 CONFIGURAR INTERCEPTORS DE LOADING
loadingService.setupAxiosInterceptors(api)

// 🔄 CONTROLE DE REFRESH TOKEN
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

// 🔐 INTERCEPTADOR DE AUTENTICAÇÃO AVANÇADO
api.interceptors.request.use(
  async(config) => {
    // 🚫 CRÍTICO: Não adicionar token para requisições de login e logout
    const isAuthEndpoint = config.url && (
      config.url.includes('/auth/login') ||
      config.url.includes('/auth/logout')
    )

    if (typeof localStorage !== 'undefined' && !isAuthEndpoint) {
      const token = localStorage.getItem('auth_token')
      if (token) {
        // Verifica se o token está expirado
        try {
          const payload = parseJwtSafely(token)
          if (payload) {
            const currentTime = Date.now() / 1000

            // Se o token expira em menos de 5 minutos, tenta renovar
            if (payload.exp && payload.exp - currentTime < 300) {
              await refreshTokenIfNeeded()
            }
          }
        } catch (error) {
          console.warn('⚠️ Erro ao verificar expiração do token:', error)
        }

        const currentToken = localStorage.getItem('auth_token')
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`
        }
      }
    }

    // Log para debug de parâmetros de simulação
    if (config.url && config.url.includes('/simulation/rates') && config.params) {
      console.log('🔍 [API INTERCEPTOR] Parâmetros na requisição HTTP:', {
        url: config.url,
        method: config.method,
        params: config.params,
        preencher_saldo_devedor: config.params.preencher_saldo_devedor,
        data_primeiro_vencimento: config.params.data_primeiro_vencimento,
        tipo_preencher_saldo: typeof config.params.preencher_saldo_devedor,
        tipo_data_primeiro_vencimento: typeof config.params.data_primeiro_vencimento
      });
    }

    return config
  },
  (error) => Promise.reject(error)
)

// 📥 INTERCEPTADOR DE RESPOSTA AVANÇADO
api.interceptors.response.use(
  (response) => {
    // Log específico para a API de mapeamento
    if (response.config.url && response.config.url.includes('/preview/mapping')) {
      // Corrigir estrutura de resposta para mapeamento
      if (response.data && response.data.success && response.data.data) {
        // Mover os dados de response.data.data para response.data para compatibilidade
        response.data.mapping = response.data.data.mapping
        response.data.validation = response.data.data.validation
        response.data.timestamp = response.data.data.timestamp
        console.log('✅ Estrutura de mapeamento corrigida')
      }
    }

    // Log específico para a API de schemas
    if (response.config.url && response.config.url.includes('/preview/schemas')) {
      // Corrigir estrutura de resposta para schemas se necessário
      if (response.data && response.data.success && response.data.data && response.data.data.schemas) {
        // Mover os schemas de response.data.data para response.data para compatibilidade
        response.data.schemas = response.data.data.schemas
        response.data.count = response.data.data.count
        response.data.timestamp = response.data.data.timestamp
        console.log('✅ Estrutura de schemas corrigida')
      }
    }

    // Log específico para a API de tabelas
    if (response.config.url && response.config.url.includes('/preview/tables') && !response.config.url.includes('/columns')) {
      // Corrigir estrutura de resposta para tabelas - mover dados para o nível superior
      if (response.data && response.data.success && response.data.data) {
        // Substituir response.data completamente com os dados corretos
        const originalData = response.data.data
        response.data = {
          success: true,
          tables: originalData.tables,
          schemas: originalData.schemas,
          totalTables: originalData.totalTables,
          timestamp: originalData.timestamp
        }
        console.log('✅ Estrutura de tabelas corrigida:', response.data)
      }
    }

    // Log específico para a API de colunas de tabela
    if (response.config.url && response.config.url.includes('/preview/tables/') && response.config.url.includes('/columns')) {
      // Corrigir estrutura de resposta para colunas - mover dados para o nível superior
      if (response.data && response.data.success && response.data.data) {
        // Substituir response.data completamente com os dados corretos
        const originalData = response.data.data
        response.data = {
          success: true,
          columns: originalData.columns,
          tableName: originalData.tableName,
          schema: originalData.schema,
          totalColumns: originalData.totalColumns,
          timestamp: originalData.timestamp
        }
        console.log('✅ Estrutura de colunas corrigida:', response.data)
      }
    }
    return response
  },
  async(error) => {
    const originalRequest = error.config
    // ✅ VERIFICA SE A REQUISIÇÃO QUE FALHOU NÃO É A DE LOGIN OU REFRESH
    const isLoginOrRefreshAttempt =
      originalRequest.url.includes('/auth/login') ||
      originalRequest.url.includes('/auth/refresh')

    // 🔄 TRATAMENTO DE TOKEN EXPIRADO
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !isLoginOrRefreshAttempt

    ) {
      if (isRefreshing) {
        // Se já está renovando, adiciona à fila
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return api(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        console.log('🔄 Token expirado, tentando renovar...')
        const newToken = await refreshToken()

        if (newToken) {
          processQueue(null, newToken)
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return api(originalRequest)
        }
      } catch (refreshError) {
        console.error('❌ Erro ao renovar token:', refreshError)
        processQueue(refreshError, null)
        await handleAuthError()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // 🚨 TRATAMENTO DE TOKEN EXPIRADO (403 com TOKEN_EXPIRED)
    if (
      error.response &&
      error.response.status === 403 &&
      error.response.data?.code === 'TOKEN_EXPIRED' &&
      !originalRequest._retry &&
      !isLoginOrRefreshAttempt
    ) {
      console.warn('🚨 Token expirado detectado via 403 TOKEN_EXPIRED')
      originalRequest._retry = true

      // Tentar refresh uma vez, se falhar, fazer logout
      try {
        const newToken = await refreshToken()
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return api(originalRequest)
        }
      } catch (refreshError) {
        console.error('❌ Refresh token falhou, fazendo logout automático')
        await handleAuthError()
        return Promise.reject(refreshError)
      }
    }

    // 🔥 USAR SISTEMA CENTRALIZADO DE TRATAMENTO DE ERROS
    // Adicionar contexto da requisição original para retry
    error.originalRequest = originalRequest

    // Usar error handler centralizado
    errorHandlingService.handleError(error, {
      component: 'API_Interceptor',
      url: originalRequest?.url,
      method: originalRequest?.method,
      originalRequest: originalRequest
    })

    return Promise.reject(error)
  }
)

// 🔄 FUNÇÃO PARA RENOVAR TOKEN
async function refreshToken() {
  try {
    if (typeof localStorage === 'undefined') return null

    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) {
      throw new Error('Refresh token não encontrado')
    }

    const response = await axios.post(`${BASE_URL}/auth/refresh`, {
      refreshToken
    }, {
      withCredentials: true
    })

    const { token, refreshToken: newRefreshToken } = response.data

    localStorage.setItem('auth_token', token)
    if (newRefreshToken) {
      localStorage.setItem('refresh_token', newRefreshToken)
    }

    console.log('✅ Token renovado com sucesso')
    return token
  } catch (error) {
    console.error('❌ Falha ao renovar token:', error)
    throw error
  }
}

// 🔄 FUNÇÃO SEGURA PARA DECODIFICAR JWT
function parseJwtSafely(token) {
  try {
    if (!token || typeof token !== 'string') {
      return null
    }

    const parts = token.split('.')
    if (parts.length !== 3) {
      console.warn('⚠️ Token JWT com formato inválido')
      return null
    }

    // Adiciona padding se necessário
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    while (base64.length % 4) {
      base64 += '='
    }

    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )

    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('❌ Erro ao decodificar JWT:', error)
    return null
  }
}

// 🔄 VERIFICA E RENOVA TOKEN SE NECESSÁRIO
async function refreshTokenIfNeeded() {
  try {
    if (typeof localStorage === 'undefined') return

    const token = localStorage.getItem('auth_token')
    if (!token) return

    const payload = parseJwtSafely(token)
    if (!payload || !payload.exp) return

    const currentTime = Date.now() / 1000

    // Se expira em menos de 5 minutos, renova
    if (payload.exp - currentTime < 300) {
      await refreshToken()
    }
  } catch (error) {
    console.warn('⚠️ Erro ao verificar necessidade de renovação:', error)
  }
}

// 🚫 TRATAMENTO DE ERRO DE AUTENTICAÇÃO
async function handleAuthError() {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user_data')
  }

  if (typeof window !== 'undefined') {
    // Notifica o usuário
    if (window.$snotify) {
      window.$snotify.warning('Sessão expirada. Faça login novamente.')
    }

    // Redireciona para login
    if (window.location.pathname !== '/login') {
      window.location.href = '/login'
    }
  }
}

// 🔐 SISTEMA DE AUTENTICAÇÃO (substitui Firebase Auth)
class AuthService {
  constructor() {
    this.currentUser = null
    this.loadUserFromStorage()
  }

  loadUserFromStorage() {
    if (typeof localStorage !== 'undefined') {
      const userData = localStorage.getItem('user_data')
      if (userData) {
        try {
          this.currentUser = JSON.parse(userData)

          // Adiciona propriedades para compatibilidade com Firebase
          if (this.currentUser && !this.currentUser.uid) {
            this.currentUser.uid = this.currentUser.id
          }
        } catch (error) {
          console.error('Erro ao carregar dados do usuário:', error)
          this.currentUser = null
        }
      }
    }
  }

  async signInWithEmailAndPassword(email, password) {
    try {
      const response = await api.post('/auth/login', { email, password })
      const { token, refreshToken, user } = response.data

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('auth_token', token)
        localStorage.setItem('refresh_token', refreshToken)
        localStorage.setItem('user_data', JSON.stringify(user))
      }

      // Adiciona compatibilidade com Firebase
      user.uid = user.id
      this.currentUser = user

      console.log('✅ Login realizado com sucesso')
      return { user }
    } catch (error) {
      console.error('❌ Erro no login:', error)
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        'Erro no login'
      throw new Error(message)
    }
  }

  async signOut() {
    try {
      // 🔑 CRÍTICO: Obter token ANTES de limpar localStorage
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null

      // 🧹 Limpar dados locais ANTES da requisição de logout
      // Isso evita que o token blacklisted seja enviado na próxima requisição
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user_data')
      }

      // 🔒 CRÍTICO: Limpar header Authorization GLOBAL
      delete api.defaults.headers.common['Authorization']

      this.currentUser = null
      console.log('✅ Sessão local limpa ANTES do logout no servidor')

      // 🚪 Fazer logout no servidor com token específico (se disponível)
      if (token) {
        // 🔒 CRÍTICO: Usar instância axios direta para evitar interceptor
        const logoutResponse = await axios.post(`${BASE_URL}/auth/logout`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 5000 // Timeout específico para logout
        })
        console.log('✅ Logout realizado no servidor')
      } else {
        console.log('⚠️ Logout local apenas - token não encontrado')
      }
    } catch (error) {
      console.warn('⚠️ Erro no logout do servidor:', error)
      // Mesmo com erro no servidor, a sessão local já foi limpa
      // Isso garante que o usuário não fique "preso" em caso de problemas de rede
    }
  }

  async getIdToken(forceRefresh = false) {
    if (typeof localStorage === 'undefined') return null

    if (forceRefresh) {
      try {
        await refreshTokenIfNeeded()
      } catch (error) {
        console.warn('⚠️ Erro ao forçar renovação:', error)
      }
    }

    return localStorage.getItem('auth_token')
  }

  async getIdTokenResult() {
    const token = await this.getIdToken()
    if (!token) return null

    try {
      const payload = parseJwtSafely(token)
      if (!payload) {
        console.error('❌ Token JWT inválido')
        return null
      }

      return {
        claims: {
          ...payload,
          // Compatibilidade com Firebase
          companyId: payload.company_id || payload.companyId,
          branchId: payload.branch_id || payload.branchId,
          profile: payload.profile || payload.role
        }
      }
    } catch (error) {
      console.error('❌ Erro ao decodificar token:', error)
      return null
    }
  }

  onAuthStateChanged(callback) {
    // Chama imediatamente com o usuário atual
    callback(this.currentUser)

    // Observa mudanças no localStorage
    if (typeof window !== 'undefined') {
      const handleStorageChange = (e) => {
        if (e.key === 'user_data') {
          this.loadUserFromStorage()
          callback(this.currentUser)
        }
      }

      window.addEventListener('storage', handleStorageChange)

      // Retorna função para remover o listener
      return () => {
        window.removeEventListener('storage', handleStorageChange)
      }
    }

    return () => {} // Fallback para ambientes sem window
  }

  onIdTokenChanged(callback) {
    return this.onAuthStateChanged(callback)
  }
}

// 📊 SISTEMA DE FIRESTORE (substitui Firestore)
class FirestoreService {
  constructor() {
    this.collections = {}
  }

  collection(name) {
    return new Collection(name)
  }

  doc(path) {
    return new Document(path)
  }
}

class Collection {
  constructor(name) {
    this.name = name
    this.query = {}
  }

  where(field, operator, value) {
    if (!this.query.where) this.query.where = []
    this.query.where.push({ field, operator, value })
    return this
  }

  orderBy(field, direction = 'asc') {
    this.query.orderBy = { field, direction }
    return this
  }

  limit(count) {
    this.query.limit = count
    return this
  }

  async get() {
    try {
      // Converter query para formato URL que o backend espera
      const params = {}

      if (this.query.where) {
        this.query.where.forEach((condition, index) => {
          params[`where[${index}][field]`] = condition.field
          params[`where[${index}][operator]`] = condition.operator
          params[`where[${index}][value]`] = condition.value
        })
      }

      if (this.query.orderBy) {
        params.orderBy = this.query.orderBy.field
        params.order = this.query.orderBy.direction
      }

      if (this.query.limit) {
        params.limit = this.query.limit
      }

      const response = await api.get(`/collections/${this.name}`, {
        params
      })

      return {
        docs: response.data.map((doc) => ({
          id: doc.id,
          data: () => doc,
          exists: true
        })),
        size: response.data.length,
        empty: response.data.length === 0
      }
    } catch (error) {
      console.error(`Erro ao buscar ${this.name}:`, error)
      throw error
    }
  }

  async add(data) {
    try {
      const response = await api.post(`/collections/${this.name}`, data)
      return {
        id: response.data.id,
        get: async() => ({
          id: response.data.id,
          data: () => response.data,
          exists: true
        })
      }
    } catch (error) {
      console.error(`Erro ao criar ${this.name}:`, error)
      throw error
    }
  }

  doc(id) {
    return new Document(`${this.name}/${id}`)
  }

  onSnapshot(callback, errorCallback) {
    // 🔌 Implementação com WebSocket + fallback para polling
    let unsubscribe = null
    let pollingInterval = null
    let isWebSocketActive = false

    // Tentar usar WebSocket primeiro - CORRIGIDO: isConnected é propriedade, não método
    const hasWebSocket = window.websocketClient &&
                        window.websocketClient.isConnected === true

    if (hasWebSocket) {
      try {
        console.log(`📡 Configurando WebSocket listener para collection: ${this.name}`)

        // Registrar listener para mudanças na collection
        const eventName = `COLLECTION_${this.name.toUpperCase()}_UPDATED`

        const wsCallback = (data) => {
          console.log(`📡 WebSocket update recebido para ${this.name}:`, data)
          // Simular estrutura de snapshot
          const snapshot = {
            docs: data.items || [],
            size: data.items ? data.items.length : 0,
            empty: !data.items || data.items.length === 0
          }
          callback(snapshot)
        }

        window.websocketClient.on(eventName, wsCallback)
        isWebSocketActive = true

        // Fazer primeira busca para dados iniciais
        this.get().then(snapshot => {
          callback(snapshot)
        }).catch(error => {
          if (errorCallback) errorCallback(error)
        })

        unsubscribe = () => {
          console.log(`📡 Removendo WebSocket listener para ${this.name}`)
          window.websocketClient.off(eventName, wsCallback)
          if (pollingInterval) {
            clearInterval(pollingInterval)
            pollingInterval = null
          }
        }
      } catch (error) {
        console.warn(`⚠️ Erro ao configurar WebSocket para ${this.name}, usando polling:`, error)
        isWebSocketActive = false
      }
    }

    // Fallback para polling se WebSocket não estiver disponível
    if (!isWebSocketActive) {
      console.log(`🔄 Usando polling para collection: ${this.name}`)

      // OTIMIZADO: Intervalo de polling mais inteligente baseado no tipo de collection
      const getPollingInterval = (collectionName) => {
        // Collections críticas: 2 minutos
        if (['customers', 'calls', 'status'].includes(collectionName)) {
          return 120000 // 2 minutos
        }
        // Collections moderadas: 5 minutos
        if (['distributions', 'campaigns', 'uploads'].includes(collectionName)) {
          return 300000 // 5 minutos
        }
        // Collections menos críticas: 10 minutos
        return 600000 // 10 minutos
      }

      const pollingIntervalTime = getPollingInterval(this.name)
      console.log(`🔄 Polling configurado para ${this.name}: ${pollingIntervalTime / 1000}s`)

      pollingInterval = setInterval(async() => {
        try {
          const snapshot = await this.get()
          callback(snapshot)
        } catch (error) {
          if (errorCallback) errorCallback(error)
        }
      }, pollingIntervalTime) // Intervalo otimizado por collection

      // Eventos globais para pausar/retomar polling conforme estado de autenticação
      const authInvalidHandler = () => {
        if (pollingInterval) {
          clearInterval(pollingInterval)
          pollingInterval = null
          console.log(`🛑 Polling de ${this.name} pausado (token inválido)`)
        }
      }

      const authRefreshedHandler = () => {
        if (!pollingInterval) {
          console.log(`▶️ Polling de ${this.name} retomado (token renovado)`)
          pollingInterval = setInterval(async() => {
            try {
              const snapshot = await this.get()
              callback(snapshot)
            } catch (error) {
              if (errorCallback) errorCallback(error)
            }
          }, pollingIntervalTime) // Intervalo otimizado por collection
        }
      }

      window.addEventListener('AUTH_INVALID', authInvalidHandler)
      window.addEventListener('AUTH_REFRESHED', authRefreshedHandler)

      unsubscribe = () => {
        window.removeEventListener('AUTH_INVALID', authInvalidHandler)
        window.removeEventListener('AUTH_REFRESHED', authRefreshedHandler)
        if (pollingInterval) {
          clearInterval(pollingInterval)
          pollingInterval = null
        }
      }
    }

    // Retorna função para cancelar o listener
    return unsubscribe
  }
}

class Document {
  constructor(path) {
    this.path = path
    const parts = path.split('/')
    this.collection = parts[0]
    this.id = parts[1]
  }

  async get() {
    try {
      const response = await api.get(
        `/collections/${this.collection}/${this.id}`
      )
      return {
        id: this.id,
        data: () => response.data,
        exists: true
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return {
          id: this.id,
          data: () => null,
          exists: false
        }
      }
      throw error
    }
  }

  async set(data, options = {}) {
    try {
      if (options.merge) {
        await api.patch(`/collections/${this.collection}/${this.id}`, data)
      } else {
        await api.put(`/collections/${this.collection}/${this.id}`, data)
      }
    } catch (error) {
      console.error(`Erro ao salvar ${this.path}:`, error)
      throw error
    }
  }

  async update(data) {
    try {
      await api.patch(`/collections/${this.collection}/${this.id}`, data)
    } catch (error) {
      console.error(`Erro ao atualizar ${this.path}:`, error)
      throw error
    }
  }

  async delete() {
    try {
      await api.delete(`/collections/${this.collection}/${this.id}`)
    } catch (error) {
      console.error(`Erro ao deletar ${this.path}:`, error)
      throw error
    }
  }

  onSnapshot(callback, errorCallback) {
    // 🔌 Implementação com WebSocket + fallback para polling
    let unsubscribe = null
    let pollingInterval = null
    let isWebSocketActive = false

    // Tentar usar WebSocket primeiro - CORRIGIDO: isConnected é propriedade, não método
    const hasWebSocket = window.websocketClient &&
                        window.websocketClient.isConnected === true

    if (hasWebSocket) {
      try {
        console.log(`📡 Configurando WebSocket listener para document: ${this.path}`)

        // Registrar listener para mudanças no documento específico
        const eventName = `DOCUMENT_${this.collection.toUpperCase()}_${this.id}_UPDATED`

        const wsCallback = (data) => {
          console.log(`📡 WebSocket update recebido para ${this.path}:`, data)
          // Simular estrutura de documento
          const doc = {
            id: this.id,
            data: () => data,
            exists: data !== null && data !== undefined
          }
          callback(doc)
        }

        window.websocketClient.on(eventName, wsCallback)
        isWebSocketActive = true

        // Fazer primeira busca para dados iniciais
        this.get().then(doc => {
          callback(doc)
        }).catch(error => {
          if (errorCallback) errorCallback(error)
        })

        unsubscribe = () => {
          console.log(`📡 Removendo WebSocket listener para ${this.path}`)
          window.websocketClient.off(eventName, wsCallback)
          if (pollingInterval) {
            clearInterval(pollingInterval)
            pollingInterval = null
          }
        }
      } catch (error) {
        console.warn(`⚠️ Erro ao configurar WebSocket para ${this.path}, usando polling:`, error)
        isWebSocketActive = false
      }
    }

    // Fallback para polling se WebSocket não estiver disponível
    if (!isWebSocketActive) {
      console.log(`🔄 Usando polling para document: ${this.path}`)
      pollingInterval = setInterval(async() => {
        try {
          const doc = await this.get()
          callback(doc)
        } catch (error) {
          if (errorCallback) errorCallback(error)
        }
      }, 60000) // Atualiza a cada 60 segundos (otimizado)

      // Eventos globais para pausar/retomar polling conforme estado de autenticação
      const authInvalidHandler = () => {
        if (pollingInterval) {
          clearInterval(pollingInterval)
          pollingInterval = null
          console.log(`🛑 Polling de ${this.path} pausado (token inválido)`)
        }
      }

      const authRefreshedHandler = () => {
        if (!pollingInterval) {
          console.log(`▶️ Polling de ${this.path} retomado (token renovado)`)
          pollingInterval = setInterval(async() => {
            try {
              const doc = await this.get()
              callback(doc)
            } catch (error) {
              if (errorCallback) errorCallback(error)
            }
          }, 60000) // Atualiza a cada 60 segundos (otimizado)
        }
      }

      window.addEventListener('AUTH_INVALID', authInvalidHandler)
      window.addEventListener('AUTH_REFRESHED', authRefreshedHandler)

      unsubscribe = () => {
        window.removeEventListener('AUTH_INVALID', authInvalidHandler)
        window.removeEventListener('AUTH_REFRESHED', authRefreshedHandler)
        if (pollingInterval) {
          clearInterval(pollingInterval)
          pollingInterval = null
        }
      }
    }

    return unsubscribe
  }
}

// 🔧 SISTEMA DE FUNCTIONS (substitui Firebase Functions)
class FunctionsService {
  constructor(region = 'us-east1') {
    this.region = region
  }

  httpsCallable(name) {
    return async(data = {}) => {
      try {
        const response = await api.post(`/functions/${name}`, data)
        return response.data
      } catch (error) {
        throw new Error(
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
            `Erro na function ${name}`
        )
      }
    }
  }
}

// 📁 SISTEMA DE STORAGE (substitui Firebase Storage)
class StorageService {
  ref(path) {
    return new StorageRef(path)
  }
}

class StorageRef {
  constructor(path) {
    this.path = path
  }

  async put(file) {
    const formData = new FormData()
    // 🔄 CRITICAL: path must be appended BEFORE file for multer to access it in req.body
    formData.append('path', this.path)
    formData.append('file', file)

    try {
      const response = await api.post('/storage/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      return {
        ref: this,
        state: 'success',
        bytesTransferred: file.size,
        totalBytes: file.size,
        metadata: response.data
      }
    } catch (error) {
      throw error
    }
  }

  async getDownloadURL() {
    try {
      const response = await api.get(
        `/storage/url/${encodeURIComponent(this.path)}`
      )
      return response.data.url
    } catch (error) {
      throw error
    }
  }

  async delete() {
    try {
      await api.delete(`/storage/${encodeURIComponent(this.path)}`)
    } catch (error) {
      throw error
    }
  }
}

// 📈 SISTEMA DE ANALYTICS (substitui Firebase Analytics)
class AnalyticsService {
  setUserId(userId) {
    // Implementar analytics se necessário
    console.log('Analytics: setUserId', userId)
  }

  setUserProperties(properties) {
    console.log('Analytics: setUserProperties', properties)
  }

  logEvent(eventName, parameters) {
    console.log('Analytics: logEvent', eventName, parameters)
  }
}

// 💓 SISTEMA DE HEARTBEAT E DETECTOR DE ATIVIDADE
class ActivityDetector {
  constructor() {
    this.isActive = false
    this.lastActivity = Date.now()
    this.heartbeatInterval = null
    this.inactivityCheckInterval = null
    this.activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    this.HEARTBEAT_INTERVAL = 5 * 60 * 1000 // 5 minutos

    // 🔧 CONFIGURAÇÕES DE INATIVIDADE (com suporte a configuração)
    this.loadInactivitySettings()

    // 🔄 Configurações de backoff exponencial
    this.retryAttempts = 0
    this.maxRetryAttempts = 5
    this.baseRetryDelay = 1000 // 1 segundo
    this.maxRetryDelay = 60000 // 1 minuto
    this.isRetrying = false
    this.lastAuthError = null

    // ⚠️ Controle de aviso de inatividade
    this.inactivityWarningShown = false
    this.inactivityWarningTimeout = null

    this.init()
  }

  /**
   * 🔧 Carregar configurações de inatividade do localStorage
   */
  loadInactivitySettings() {
    try {
      const settings = localStorage.getItem('inactivity_settings')
      if (settings) {
        const parsed = JSON.parse(settings)
        this.ACTIVITY_TIMEOUT = parsed.timeout * 60 * 1000 // Converter minutos para ms
        this.AUTO_LOGOUT_ENABLED = parsed.enabled !== false
        this.WARNING_TIME = 30 * 1000 // 30 segundos antes do logout
        console.log('⚙️ Configurações de inatividade carregadas:', {
          timeout: parsed.timeout + ' minutos',
          enabled: this.AUTO_LOGOUT_ENABLED
        })
      } else {
        // Valores padrão
        this.ACTIVITY_TIMEOUT = 30 * 60 * 1000 // 30 minutos
        this.AUTO_LOGOUT_ENABLED = true
        this.WARNING_TIME = 30 * 1000 // 30 segundos
      }
    } catch (error) {
      console.warn('⚠️ Erro ao carregar configurações de inatividade, usando padrões', error)
      this.ACTIVITY_TIMEOUT = 30 * 60 * 1000 // 30 minutos
      this.AUTO_LOGOUT_ENABLED = true
      this.WARNING_TIME = 30 * 1000
    }
  }

  /**
   * 🔄 Atualizar configurações de inatividade
   */
  updateInactivitySettings(timeoutMinutes, enabled = true) {
    this.ACTIVITY_TIMEOUT = timeoutMinutes * 60 * 1000
    this.AUTO_LOGOUT_ENABLED = enabled

    // Salvar no localStorage
    localStorage.setItem('inactivity_settings', JSON.stringify({
      timeout: timeoutMinutes,
      enabled: enabled
    }))

    console.log('✅ Configurações de inatividade atualizadas:', {
      timeout: timeoutMinutes + ' minutos',
      enabled: enabled
    })

    // Reiniciar monitoramento
    this.startInactivityMonitoring()
  }

  init() {
    if (typeof window === 'undefined') return

    // Detecta atividade do usuário
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.onActivity.bind(this), true)
    })

    // Inicia o sistema de heartbeat
    this.startHeartbeat()

    // 🚪 Inicia monitoramento de inatividade para logout automático
    this.startInactivityMonitoring()

    console.log('💓 Sistema de heartbeat e monitoramento de inatividade iniciados')
  }

  /**
   * 🚪 Iniciar monitoramento de inatividade
   */
  startInactivityMonitoring() {
    // Limpar intervalo anterior se existir
    if (this.inactivityCheckInterval) {
      clearInterval(this.inactivityCheckInterval)
    }

    // Limpar timeout de aviso anterior se existir
    if (this.inactivityWarningTimeout) {
      clearTimeout(this.inactivityWarningTimeout)
      this.inactivityWarningTimeout = null
      this.inactivityWarningShown = false
    }

    // Não iniciar se logout automático estiver desabilitado
    if (!this.AUTO_LOGOUT_ENABLED) {
      console.log('🔓 Logout automático por inatividade DESABILITADO')
      return
    }

    console.log(`⏰ Monitoramento de inatividade iniciado (timeout: ${this.ACTIVITY_TIMEOUT / 60000} min)`)

    // Verificar a cada 10 segundos
    this.inactivityCheckInterval = setInterval(() => {
      this.checkInactivity()
    }, 10000)
  }

  /**
   * ⏰ Verificar inatividade e executar logout se necessário
   */
  async checkInactivity() {
    // Não verificar se não tiver token (usuário não logado)
    const token = localStorage.getItem('auth_token')
    if (!token) return

    const now = Date.now()
    const timeSinceActivity = now - this.lastActivity
    const timeUntilLogout = this.ACTIVITY_TIMEOUT - timeSinceActivity

    // 🚨 MOSTRAR AVISO 30 SEGUNDOS ANTES DO LOGOUT
    if (timeUntilLogout <= this.WARNING_TIME && timeUntilLogout > 0 && !this.inactivityWarningShown) {
      this.showInactivityWarning(Math.ceil(timeUntilLogout / 1000))
      this.inactivityWarningShown = true
    }

    // 🚪 FAZER LOGOUT AUTOMÁTICO
    if (timeSinceActivity >= this.ACTIVITY_TIMEOUT) {
      console.log('🚪 Logout automático por inatividade após', this.ACTIVITY_TIMEOUT / 60000, 'minutos')
      await this.performAutoLogout()
    }
  }

  /**
   * ⚠️ Mostrar aviso de inatividade
   */
  showInactivityWarning(secondsRemaining) {
    console.log(`⚠️ Aviso de inatividade: ${secondsRemaining}s restantes`)

    // Emitir evento global para o Vue capturar
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('INACTIVITY_WARNING', {
        detail: {
          secondsRemaining,
          message: `Você será desconectado em ${secondsRemaining} segundos por inatividade`
        }
      }))
    }
  }

  /**
   * 🚪 Executar logout automático
   * 🚀 OTIMIZADO: Garante invalidação de sessão mesmo se token estiver expirado
   */
  async performAutoLogout() {
    console.log('🚪 Executando logout automático por inatividade')

    try {
      // Emitir evento antes de fazer logout
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('AUTO_LOGOUT', {
          detail: {
            reason: 'inactivity',
            message: 'Você foi desconectado por inatividade'
          }
        }))
      }

      // Parar monitoramento
      this.stop()

      // 🚀 TENTAR LOGOUT VIA API (com fallback para logout por email)
      try {
        const token = localStorage.getItem('auth_token')
        let userEmail = null

        // Tentar extrair email do user_data antes de limpar localStorage
        try {
          const userDataStr = localStorage.getItem('user_data')
          if (userDataStr) {
            const userData = JSON.parse(userDataStr)
            userEmail = userData?.email
          }
        } catch (e) {
          console.warn('⚠️ Não foi possível extrair email do user_data')
        }

        // Tentar logout normal primeiro (se token ainda válido)
        if (token) {
          try {
            await api.post('/auth/logout', {}, {
              headers: { Authorization: `Bearer ${token}` }
            })
            console.log('✅ Logout via token realizado com sucesso')
          } catch (tokenError) {
            // Se token expirou, usar logout por email
            console.warn('⚠️ Token expirado, usando logout por email')
            if (userEmail) {
              await api.post('/auth/logout-inactivity', { email: userEmail })
              console.log('✅ Logout por inatividade realizado com sucesso')
            }
          }
        } else if (userEmail) {
          // Se não tem token mas tem email, usar logout por email
          await api.post('/auth/logout-inactivity', { email: userEmail })
          console.log('✅ Logout por inatividade (sem token) realizado com sucesso')
        }
      } catch (error) {
        console.warn('⚠️ Erro ao fazer logout via API:', error.message)
        // Continuar mesmo com erro - limpar dados locais de qualquer forma
      }

      // Limpar dados locais (sempre executar, mesmo se API falhar)
      localStorage.clear()

      // Redirecionar para login após um pequeno delay
      setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.location.href = '/login?reason=inactivity'
        }
      }, 500)
    } catch (error) {
      console.error('❌ Erro ao executar logout automático:', error)
      // Garantir limpeza mesmo em caso de erro
      localStorage.clear()
      if (typeof window !== 'undefined') {
        window.location.href = '/login?reason=inactivity'
      }
    }
  }

  onActivity() {
    this.lastActivity = Date.now()
    if (!this.isActive) {
      this.isActive = true
      console.log('👤 Usuário ativo detectado')
    }

    // 🔄 Resetar aviso de inatividade quando houver atividade
    if (this.inactivityWarningShown) {
      this.inactivityWarningShown = false
      console.log('✅ Aviso de inatividade resetado - usuário voltou a ser ativo')

      // Emitir evento para fechar modal/notificação
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('INACTIVITY_CANCELLED'))
      }
    }
  }

  startHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
    }

    this.heartbeatInterval = setInterval(async() => {
      await this.checkAndSendHeartbeat()
    }, this.HEARTBEAT_INTERVAL)
  }

  async checkAndSendHeartbeat() {
    try {
      const now = Date.now()
      const timeSinceActivity = now - this.lastActivity

      // Se usuário está inativo há mais de 30 minutos, para o heartbeat
      if (timeSinceActivity > this.ACTIVITY_TIMEOUT) {
        console.log('😴 Usuário inativo há mais de 30 minutos, parando heartbeat')
        this.isActive = false
        return
      }

      // Se houve atividade nos últimos 5 minutos, envia heartbeat
      if (timeSinceActivity < this.HEARTBEAT_INTERVAL && this.isActive) {
        await this.sendHeartbeat()
      }
    } catch (error) {
      console.error('❌ Erro no heartbeat:', error)
    }
  }

  async sendHeartbeat() {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        console.log('🚫 Sem token, pulando heartbeat')
        this.resetRetryState()
        return
      }

      console.log('💓 Enviando heartbeat...')
      const response = await api.post('/auth/heartbeat')

      if (response.data.data.renewed) {
        const newToken = response.data.data.token
        localStorage.setItem('auth_token', newToken)
        console.log('✅ Token renovado via heartbeat')
      } else {
        console.log('ℹ️ Token ainda válido, não renovado')
      }

      // Reset retry state em caso de sucesso
      this.resetRetryState()
    } catch (error) {
      console.error('❌ Erro no heartbeat:', error)

      // Se erro 401, implementar backoff exponencial
      if (error.response && error.response.status === 401) {
        await this.handleAuthErrorWithBackoff(error)
      } else {
        // Para outros erros, apenas log
        console.error('❌ Erro não relacionado à autenticação:', error.message)
      }
    }
  }

  /**
   * 🔄 Tratamento de erro de autenticação com backoff exponencial
   */
  async handleAuthErrorWithBackoff(error) {
    this.lastAuthError = error
    this.retryAttempts++

    // Emitir evento global para pausar polling
    try {
      window.dispatchEvent(new CustomEvent('AUTH_INVALID', { detail: { source: 'heartbeat', attempt: this.retryAttempts } }))
    } catch (e) {
      // noop
    }

    console.log(`🔄 Tentativa ${this.retryAttempts}/${this.maxRetryAttempts} de recuperação de autenticação`)

    if (this.retryAttempts >= this.maxRetryAttempts) {
      console.log('🚫 Máximo de tentativas atingido, redirecionando para login')
      this.resetRetryState()
      await handleAuthError()
      return
    }

    if (this.isRetrying) {
      console.log('⏳ Já está tentando recuperar autenticação, aguardando...')
      return
    }

    this.isRetrying = true

    // Calcular delay com backoff exponencial
    const delay = Math.min(
      this.baseRetryDelay * Math.pow(2, this.retryAttempts - 1),
      this.maxRetryDelay
    )

    console.log(`⏱️ Aguardando ${delay}ms antes da próxima tentativa...`)

    // Parar heartbeat durante o retry
    this.pauseHeartbeat()

    setTimeout(async() => {
      try {
        // Tentar refresh do token
        await this.attemptTokenRefresh()

        // Se sucesso, reiniciar heartbeat
        this.resetRetryState()
        this.resumeHeartbeat()
        console.log('✅ Autenticação recuperada com sucesso')

        // Emitir evento global para retomar polling
        try {
          window.dispatchEvent(new CustomEvent('AUTH_REFRESHED', { detail: { source: 'heartbeat' } }))
        } catch (e) {
          // noop
        }
      } catch (refreshError) {
        console.error('❌ Falha na recuperação do token:', refreshError)
        this.isRetrying = false

        // Tentar novamente se ainda há tentativas
        if (this.retryAttempts < this.maxRetryAttempts) {
          await this.handleAuthErrorWithBackoff(refreshError)
        } else {
          console.log('🚫 Todas as tentativas falharam, redirecionando para login')
          this.resetRetryState()
          await handleAuthError()
        }
      }
    }, delay)
  }

  /**
   * 🔄 Tentar refresh do token
   */
  async attemptTokenRefresh() {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) {
      throw new Error('Sem refresh token disponível')
    }

    try {
      const response = await api.post('/auth/refresh', {
        refreshToken: refreshToken
      })

      if (response.data.data.token) {
        localStorage.setItem('auth_token', response.data.data.token)
        if (response.data.data.refreshToken) {
          localStorage.setItem('refresh_token', response.data.data.refreshToken)
        }
        console.log('✅ Token atualizado via refresh')
        return true
      }
    } catch (error) {
      console.error('❌ Erro no refresh do token:', error)
      throw error
    }
  }

  /**
   * 🔄 Reset do estado de retry
   */
  resetRetryState() {
    this.retryAttempts = 0
    this.isRetrying = false
    this.lastAuthError = null
  }

  /**
   * ⏸️ Pausar heartbeat temporariamente
   */
  pauseHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
      console.log('⏸️ Heartbeat pausado para retry')
    }
  }

  /**
   * ▶️ Retomar heartbeat
   */
  resumeHeartbeat() {
    if (!this.heartbeatInterval) {
      this.startHeartbeat()
      console.log('▶️ Heartbeat retomado após recuperação')
    }
  }

  stop() {
    // Parar heartbeat
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }

    // 🚪 Parar monitoramento de inatividade
    if (this.inactivityCheckInterval) {
      clearInterval(this.inactivityCheckInterval)
      this.inactivityCheckInterval = null
    }

    // Limpar timeout de aviso
    if (this.inactivityWarningTimeout) {
      clearTimeout(this.inactivityWarningTimeout)
      this.inactivityWarningTimeout = null
    }

    // Remover event listeners
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, this.onActivity.bind(this), true)
    })

    console.log('💓 Sistema de heartbeat e monitoramento de inatividade parados')
  }
}

// 🎯 INSTÂNCIAS DOS SERVIÇOS
const auth = new AuthService()
const db = new FirestoreService()
const functions = new FunctionsService('us-east1')
const functionsSP = new FunctionsService('southamerica-east1')
const storage = new StorageService()
const analytics = new AnalyticsService()
const activityDetector = new ActivityDetector()

// 🌍 COMPATIBILIDADE GLOBAL
if (typeof window !== 'undefined') {
  window.auth = auth
  window.activityDetector = activityDetector // Para acesso global nas configurações
}

// 📤 EXPORTAÇÕES (mantém mesma interface do Firebase)
export {
  auth,
  db,
  functions,
  functionsSP,
  storage,
  analytics,
  activityDetector, // Sistema de heartbeat
  api // API direta para casos especiais
}

// Firebase core simulado para compatibilidade
export const core = {
  auth: () => auth,
  firestore: () => db,
  functions: () => functions,
  storage: () => storage,
  analytics: () => analytics
}

export const app = {
  auth: () => auth,
  firestore: () => db,
  functions: (region) =>
    region === 'southamerica-east1' ? functionsSP : functions,
  storage: () => storage,
  analytics: () => analytics
}
